import React, { useEffect, useState } from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import axios from 'axios';
import config from '../../../config';
import { getPrice } from '../../../services/priceUtils';

const Invoice = ({ id, hotel, orderData }) => {
  const [localOrderData, setLocalOrderData] = useState(null);
  const [prices, setPrices] = useState({});
  const userData = JSON.parse(localStorage.getItem('user_data'));

  useEffect(() => {
    if (orderData) {
      setLocalOrderData(orderData);
    } else {
      const fetchOrderDetails = async () => {
        try {
          const response = await axios.get(
            `${config.API_BASE_URL_V1}order/booking-detail/${id}`,
            {
              headers: {
                Authorization: `Bearer ${userData.token}`,
              },
            }
          );
          setLocalOrderData(response.data.data.booking);
        } catch (error) {
          console.error('Error fetching order details:', error);
        }
      };
      fetchOrderDetails();
    }
  }, [id, orderData, userData.token]);
  useEffect(() => {
    calculatePrices();
  }, [localOrderData]);

  const calculatePrices = async () => {
    if (!localOrderData || !userData?.token) return;

    try {
      const newPrices = {};
      const hotelId = hotel.id;
      const totalPrice = hotel.currency?.code === 'EUR' 
        ? hotel.total_with_markup_price 
        : await getPrice(
            hotel.total_with_markup_price,
            userData.token,
            'EUR',
            null,
            null,
            hotel.currency?.code
          );

      const cancellationPrice = hotel.items?.[0]?.cancellation_policies?.[0]?.amount
        ? hotel.currency?.code === 'EUR'
          ? hotel.items[0].cancellation_policies[0].amount
          : await getPrice(
              hotel.items[0].cancellation_policies[0].amount,
              userData.token,
              'EUR',
              null,
              null,
              hotel.currency?.code
            )
        : "0.00";

      newPrices[hotelId] = {
        total: totalPrice || "0.00",
        cancellation: cancellationPrice || "0.00"
      };
      
      setPrices(newPrices);
    } catch (error) {
      console.error("Error calculating prices:", error);
      setPrices({});
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    });
  };

  const data = localOrderData || {};
  if (!data) return <Text>Loading...</Text>;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <View style={styles.headerRow}>
            <Image src="/assets/logo-1.png" style={styles.logo} />
            <View style={styles.invoiceDetails}>
              <Text style={styles.textDefault}>
                Invoice No: <Text style={styles.textPrimary}>#{data.order_number?.toLowerCase() || '-'}</Text>
              </Text>
              <Text style={styles.textDefault}>
                Created Date: {data.order_datetime ? new Date(data.order_datetime).toLocaleDateString() : '-'}
              </Text>
            </View>
          </View>
        </View>

        <View style={[styles.section, { marginBottom: '50px' }]}>
          <View style={styles.headerRow}>
            <View>
              <Text style={styles.textDefault}>{data.agency?.name?.toLowerCase() || 'cornleth travel & booking system'}</Text>
              <Text style={styles.textDefault}>Email: {data.agency?.email?.toLowerCase() || 'info@cornleth.com'}</Text>
              <Text style={styles.textDefault}>Phone: {data.agency?.phone?.toLowerCase() || '+90 212 533 31 22'}</Text>
            </View>
            <View>
              <Text style={styles.textDefault}>
                {data.holder_info?.name?.toLowerCase() || '-'} {data.holder_info?.surname?.toLowerCase() || '-'}
              </Text>
              <Text style={styles.textDefault}>
                Email: {data.delivery_email?.toLowerCase() || '-'}
              </Text>
              <Text style={styles.textDefault}>
                Phone: {data.delivery_phone?.toLowerCase() || '-'}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Hotel Information</Text>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.textDefault}>Hotel Name</Text>
              <Text style={styles.textPrimary}>{hotel.name?.toLowerCase() || '-'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.textDefault}>Category</Text>
              <Text style={styles.textPrimary}>{hotel.category?.description?.toLowerCase() || '-'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.textDefault}>Address</Text>
              <Text style={styles.textPrimary}>{hotel.address?.toLowerCase() || '-'}</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Booking Info</Text>
          <View style={styles.row}>
            <View style={styles.col}>
              <Text style={styles.textDefault}>Booking Number</Text>
              <Text style={styles.textPrimary}>{data.order_number || '-'}</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.textDefault}>Booking Date</Text>
              <Text style={styles.textPrimary}>
                {data.order_datetime ? new Date(data.order_datetime).toLocaleDateString() : '-'}
              </Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.textDefault}>Status</Text>
              <Text style={styles.textPrimary}>
                {hotel.status ? hotel.status.toLowerCase() : '-'}
              </Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.textDefault}>Total Price</Text>
              <Text style={styles.textPrimary}>
                {prices[hotel.id]?.total || "0.00"} {hotel.currency?.code?.toLowerCase() || 'eur'}
              </Text>
            </View>
          </View>
        </View>

        {hotel.items?.map((item, itemIndex) => (
          <View key={item.id} style={styles.section}>
            <Text style={styles.sectionTitle}>Booking Details</Text>
            <View style={styles.card}>
              <Text style={styles.textPrimary}>{item.room_name?.toLowerCase() || '-'}</Text>
              <Text style={styles.textDefault}>
                <Text style={styles.textBold}>Board Type:</Text> {item.board_name?.toLowerCase() || '-'}
              </Text>
              <Text style={styles.textDefault}>
                <Text style={styles.textBold}>Check-In:</Text> {formatDate(item.check_in_date)}
              </Text>
              <Text style={styles.textDefault}>
                <Text style={styles.textBold}>Check-Out:</Text> {formatDate(item.check_out_date)}
              </Text>
              <Text style={styles.textDefault}>
                <Text style={styles.textBold}>Nights:</Text> {item.nights || '0'}
              </Text>
              <Text style={styles.textDefault}>
                <Text style={styles.textBold}>Guests:</Text> {item.adults_count || '0'} adults, {item.children_count || '0'} children
              </Text>
              {item.discounts?.length > 0 && (
                <View style={styles.discountsSection}>
                  {item.discounts.map((discount, index) => (
                    <Text key={index} style={styles.textDefault}>
                      {discount.name?.toLowerCase()}: {discount.amount} {item.currency?.code?.toLowerCase() || 'eur'}
                    </Text>
                  ))}
                </View>
              )}
              <Text style={styles.textDefault}>
                <Text style={styles.textBold}>Total Price:</Text> {item.with_markup_price || '0.00'} {item.currency?.code?.toLowerCase() || 'eur'}
              </Text>
            </View>
          </View>
        ))}

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Cancellation Policy</Text>
          <View style={styles.policyContainer}>
            {hotel.items?.[0]?.cancellation_policies?.length > 0 ? (
              <View style={styles.policyRow}>
                <View style={[styles.policyBadge, { backgroundColor: '#E8F5E9', flex: 1, marginRight: 5 }]}>
                  <View style={[styles.badgeIcon, { backgroundColor: '#4CAF50' }]}>
                    <Text style={styles.badgeIconText}>✓</Text>
                  </View>
                  <View style={styles.badgeContent}>
                    <Text style={[styles.badgeTitle, { color: '#2E7D32' }]}>Free Cancellation Period</Text>
                    <Text style={[styles.badgeText, { color: '#1B5E20' }]}>
                      Cancel without any charges until {formatDate(hotel.items[0].cancellation_policies[0].from)}
                    </Text>
                  </View>
                </View>

                <View style={[styles.policyBadge, { backgroundColor: '#FFF3E0', flex: 1, marginRight: 5 }]}>
                  <View style={[styles.badgeIcon, { backgroundColor: '#FF9800' }]}>
                    <Text style={styles.badgeIconText}>!</Text>
                  </View>
                  <View style={styles.badgeContent}>
                    <Text style={[styles.badgeTitle, { color: '#E65100' }]}>Cancellation Fee Period</Text>
                    <Text style={[styles.badgeText, { color: '#F57C00' }]}>
                      From {formatDate(hotel.items[0].cancellation_policies[0].from)} until {formatDate(hotel.items[0].check_in_date)}
                      {"\n"}Fee: {prices[hotel.id]?.cancellation || "0.00"} {hotel.currency?.code?.toLowerCase() || 'eur'}
                    </Text>
                  </View>
                </View>

                <View style={[styles.policyBadge, { backgroundColor: '#FFEBEE', flex: 1 }]}>
                  <View style={[styles.badgeIcon, { backgroundColor: '#F44336' }]}>
                    <Text style={styles.badgeIconText}>✕</Text>
                  </View>
                  <View style={styles.badgeContent}>
                    <Text style={[styles.badgeTitle, { color: '#D32F2F' }]}>Non-Refundable Period</Text>
                    <Text style={[styles.badgeText, { color: '#B71C1C' }]}>
                      After {formatDate(hotel.items[0].check_in_date)}
                      {"\n"}Full amount will be charged
                    </Text>
                  </View>
                </View>
              </View>
            ) : (
              <View style={styles.policyRow}>
                <View style={[styles.policyBadge, { backgroundColor: '#FFEBEE', flex: 1 }]}>
                  <View style={[styles.badgeIcon, { backgroundColor: '#F44336' }]}>
                    <Text style={styles.badgeIconText}>✕</Text>
                  </View>
                  <View style={styles.badgeContent}>
                    <Text style={[styles.badgeTitle, { color: '#D32F2F' }]}>Non-Refundable Booking</Text>
                    <Text style={[styles.badgeText, { color: '#B71C1C' }]}>
                      Non-refundable - If cancelled, full charge: <Text style={[styles.textBold, { color: '#B71C1C', fontSize: 12 }]}>{prices[hotel.id]?.total || "0.00"} {hotel.currency?.code?.toLowerCase() || 'eur'}</Text> - No refund
                    </Text>
                  </View>
                </View>
              </View>
            )}
          </View>
        </View>

        <View style={styles.footer}>
          <Text>Payment Made Via {hotel.items?.[0]?.payment_method ? hotel.items[0].payment_method.toUpperCase() : 'Bank Transfer'}</Text>
          <Text>
            Booking Reference: {data.order_number || '-'} | Agency: {data.agency?.name || '-'}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontFamily: 'Helvetica',
    flexDirection: 'column',
    height: '100%',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
  },
  header: {
    marginBottom: 10,
    paddingBottom: 5,
    borderBottom: '1px solid #ccc',
  },
  logo: {
    width: 170,
    height: 50,
    objectFit: 'contain',
  },
  section: {
    marginBottom: 10,
    paddingBottom: 5,
    borderBottom: '1px solid #ccc',
  },
  sectionTitle: {
    fontSize: 9,
    marginBottom: 5,
    fontWeight: 'bold',
    color: 'black',
    textTransform: 'uppercase',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 5,
  },
  col: {
    flex: 1,
    marginRight: 8,
  },
  card: {
    padding: 5,
    marginBottom: 5,
    border: '1px solid #ddd',
    borderRadius: 3,
  },
  textDefault: {
    fontSize: 10,
    marginBottom: 5,
    color: '#333',
    textTransform: 'capitalize',
  },
  textPrimary: {
    color: 'black',
    fontWeight: 'bold',
    fontSize: 10,
    marginBottom: 5,
    textTransform: 'capitalize',
  },
  textBold: {
    fontWeight: 'bold',
  },
  footer: {
    marginTop: 'auto',
    textAlign: 'center',
    fontSize: 8,
  },
  invoiceDetails: {
    alignItems: 'flex-end',
  },
  policyContainer: {
    marginTop: 5,
    marginBottom: 8,
  },
  policyRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  policyBadge: {
    flexDirection: 'row',
    padding: 4,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    minHeight: 50,
  },
  badgeIcon: {
    width: 16,
    height: 16,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 4,
  },
  badgeIconText: {
    color: '#FFFFFF',
    fontSize: 9,
    fontWeight: 'bold',
  },
  badgeContent: {
    flex: 1,
  },
  badgeTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 1,
  },
  badgeText: {
    fontSize: 9,
    lineHeight: 1.1,
  },
  discountsSection: {
    marginBottom: 5,
    borderBottom: '1px solid #eee',
  },
});

export default Invoice;
